/** Sentinel URL value used by group headings and fragment wrappers. */
export const HASH_URL_SENTINEL = '#';

/**
 * Returns the navigationItems array for a nav item, or an empty array
 * if navigationItems is null, undefined, or not an array.
 *
 * @param {NavigationItem} item - Navigation item
 * @returns {NavigationItem[]}
 */
export const safeNavItems = (item) => {
	if (!item) return [];
	return Array.isArray(item.navigationItems) ? item.navigationItems : [];
};

/**
 * Returns a stable key for a navigation item.
 * Prefers id, falls back to url, then label.
 *
 * @param {NavigationItem} item - Navigation item
 * @param {number} index - Array index, appended to guarantee uniqueness
 * @returns {string}
 */
export const getNavItemKey = (item, index) => {
	if (!item) return `unknown-${index}`;
	return `${item.id || item.url || item.label}-${index}`;
};

/**
 * Normalizes a URL path for comparison by stripping trailing slashes,
 * removing index.htm/index.html suffixes, and lowercasing.
 * Query strings and hash fragments are removed.
 *
 * @param {string} path
 * @returns {string}
 */
export const normalizePath = (path) => {
	if (!path) return '';
	// Remove query string and hash
	let normalized = path.split('?')[0].split('#')[0];
	// Remove trailing slash (but keep root '/')
	if (normalized.length > 1 && normalized.endsWith('/')) {
		normalized = normalized.slice(0, -1);
	}
	// Remove index.htm or index.html suffix
	normalized = normalized.replace(/\/index\.html?$/i, '');
	// Lowercase for case-insensitive comparison
	return normalized.toLowerCase();
};

/**
 * Extracts query parameters from a URL string as sorted [key, value] pairs.
 * Keys are lowercased; values are decoded. Handles multi-value keys.
 *
 * @param {string} url - URL or path with optional query string
 * @returns {Array<[string, string]>} Sorted array of [key, value] pairs
 */
export const extractQueryParams = (url) => {
	if (!url) return [];
	const queryIndex = url.indexOf('?');
	if (queryIndex === -1) return [];
	const searchParams = new URLSearchParams(url.slice(queryIndex));
	const pairs = [];
	searchParams.forEach((value, key) => {
		pairs.push([key.toLowerCase(), value]);
	});
	pairs.sort((a, b) => a[0].localeCompare(b[0]) || a[1].localeCompare(b[1]));
	return pairs;
};

/**
 * Returns true if every query parameter in itemUrl exists in pageUrl.
 * If itemUrl has no query params, returns true (trivially a subset).
 * Handles multi-value keys: every individual key=value pair in itemUrl
 * must appear in pageUrl.
 *
 * @param {string} itemUrl - Nav item URL
 * @param {string} pageUrl - Current page URL
 * @returns {boolean}
 */
export const isQueryParamsSubset = (itemUrl, pageUrl) => {
	const itemParams = extractQueryParams(itemUrl);
	if (itemParams.length === 0) return true;
	const pageParams = extractQueryParams(pageUrl);
	// For each item param, find a matching page param (consuming it to handle dupes)
	const remaining = [...pageParams];
	return itemParams.every(([iKey, iVal]) => {
		const idx = remaining.findIndex(
			([pKey, pVal]) => pKey === iKey && pVal === iVal
		);
		if (idx === -1) return false;
		remaining.splice(idx, 1);
		return true;
	});
};

/**
 * Returns true if the itemUrl matches the pageUrl.
 * Step 1: Normalized paths must match (strips index.htm, trailing slash, etc.)
 * Step 2: If itemUrl has query params, they must be a subset of pageUrl's params.
 *
 * @param {string} itemUrl - Nav item URL
 * @param {string} pageUrl - Current page URL
 * @returns {boolean}
 */
export const isUrlMatch = (itemUrl, pageUrl) => {
	if (!itemUrl || !pageUrl) return false;
	if (normalizePath(itemUrl) !== normalizePath(pageUrl)) return false;
	return isQueryParamsSubset(itemUrl, pageUrl);
};

/**
 * Converts a string to a URL-safe slug.
 * Lowercases, replaces non-alphanumeric runs with hyphens, strips leading/trailing
 * hyphens, and treats the literal string "blank" (from labelAlias sentinel values)
 * as empty.
 *
 * @param {string} str
 * @returns {string}
 */
const slugify = (str) => {
	if (!str || typeof str !== 'string') return '';
	const s = str
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-|-$/g, '');
	return s === 'blank' ? '' : s;
};

/**
 * Returns true when an id is a 32-character lowercase hex string (UUID without
 * dashes, as produced by the nav service).
 *
 * @param {string} id
 * @returns {boolean}
 */
const isUuidId = (id) => /^[0-9a-f]{32}$/i.test(id);

/**
 * Builds the href for a nav link by appending a unique `_ddc_nav_drawer` query
 * parameter to disambiguate items that share the same destination URL.
 *
 * Returns the url unchanged when it is falsy or HASH_URL_SENTINEL (drilldown triggers have no
 * real destination and must not receive the param).
 *
 * Param value strategy:
 * - Semantic ID (e.g. FINANCE_LEAD_NEW) → slugified: "finance-lead-new"
 * - UUID ID (32-char hex) → slugify(labelAlias || label) + "-" + first 6 hex
 *   chars of the ID for human readability; falls back to first 8 chars of the
 *   UUID when no usable label exists.
 * - No ID → String(item.index) as a positional fallback.
 *
 * @param {NavigationItem} item
 * @returns {string}
 */
export const buildNavUrl = (item) => {
	const { url, id, index, labelAlias, label } = item;
	if (!url || url === HASH_URL_SENTINEL) return url;
	// Only append param when we have a meaningful key; items with no id/index
	// are not rendered directly as links and need no disambiguation param.
	if (id == null && index == null) return url;

	let key;
	if (id != null) {
		if (isUuidId(id)) {
			const slug = slugify(labelAlias) || slugify(label);
			const shortId = id.slice(0, 6);
			key = slug ? `${slug}-${shortId}` : id.slice(0, 8);
		} else {
			// Semantic ID — slugify preserves readability (FINANCE_LEAD_NEW → finance-lead-new)
			key = slugify(id) || id;
		}
	} else {
		key = String(index);
	}

	const separator = url.includes('?') ? '&' : '?';
	return `${url}${separator}_ddc_nav_drawer=${encodeURIComponent(key)}`;
};

/**
 * Returns true if the navigation item is in an active/current state.
 * Checks server-side flags first (selected/matched), then falls back
 * to client-side URL matching against the current page URL.
 *
 * NOTE: item.matched is intentionally kept here. The nav service may set it
 * on multiple items when the same URL appears in more than one subtree, but
 * multi-highlight is prevented at the section level by resolveDirectNavActiveSectionId
 * in NavItemList, which uses URL-only matching to pick the single correct section
 * when _ddc_nav_drawer is present.
 *
 * @param {NavigationItem} item - Navigation item
 * @param {string} [currentPath] - Current page URL (path + query string). Defaults to window.location.pathname + window.location.search.
 * @returns {boolean}
 */
export const isNavItemActive = (item, currentPath) => {
	if (!item) return false;

	// Server-side flags take precedence
	if (item.selected || item.matched) return true;

	// Client-side URL fallback — use buildNavUrl so each item has a unique param
	const itemUrl = buildNavUrl(item);
	if (!itemUrl || itemUrl === HASH_URL_SENTINEL) return false;

	let pageUrl = currentPath;
	if (pageUrl === undefined) {
		if (typeof window === 'undefined') return false;
		pageUrl = window.location.pathname + window.location.search;
	}

	return isUrlMatch(itemUrl, pageUrl);
};

/**
 * Returns true if the item's URL matches the page URL using URL-only matching
 * (no matched/selected flags, no _ddc_nav_drawer param appended).
 * Used internally by resolveDirectNavActiveSectionId to identify which
 * section contains the clicked item when _ddc_nav_drawer is present.
 *
 * @param {NavigationItem} item
 * @param {string} currentPath - Current page URL (pathname + search)
 * @returns {boolean}
 */
const isNavItemActiveUrlOnly = (item, currentPath) => {
	if (!item) return false;
	const itemUrl = buildNavUrl(item);
	if (!itemUrl || itemUrl === HASH_URL_SENTINEL) return false;
	return isUrlMatch(itemUrl, currentPath);
};

/**
 * Returns true if any descendant of the given item matches via URL-only
 * matching (ignores matched/selected flags).
 *
 * @param {NavigationItem} item
 * @param {string} currentPath - Current page URL (pathname + search)
 * @returns {boolean}
 */
const hasUrlOnlyActiveDescendant = (item, currentPath) => {
	if (!item) return false;
	const children = safeNavItems(item);
	return children.some(
		(child) =>
			isNavItemActiveUrlOnly(child, currentPath) ||
			hasUrlOnlyActiveDescendant(child, currentPath)
	);
};

/**
 * Returns true if the item's URL matches the given normalized page path,
 * ignoring query parameters on both sides.
 *
 * Used as a fallback for direct navigation (no _ddc_nav_drawer in the URL).
 * Unlike isNavItemActive this deliberately ignores the disambiguation param
 * so that bookmarked / search-engine URLs still produce an active section.
 *
 * @param {NavigationItem} item
 * @param {string} normalizedPagePath - Already-normalized page path (use normalizePath first)
 * @returns {boolean}
 */
export const isNavItemActivePathOnly = (item, normalizedPagePath) => {
	if (!item) return false;
	if (item.selected) return true;
	const { url } = item;
	if (!url || url === HASH_URL_SENTINEL) return false;
	return normalizePath(url) === normalizedPagePath;
};

/**
 * Returns true if any descendant of the given item matches the page path
 * using path-only comparison (ignores _ddc_nav_drawer).
 *
 * @param {NavigationItem} item
 * @param {string} normalizedPagePath - Already-normalized page path
 * @returns {boolean}
 */
export const hasPathOnlyActiveDescendant = (item, normalizedPagePath) => {
	if (!item) return false;
	const children = safeNavItems(item);
	return children.some(
		(child) =>
			isNavItemActivePathOnly(child, normalizedPagePath) ||
			hasPathOnlyActiveDescendant(child, normalizedPagePath)
	);
};

/**
 * Resolves the id of the single top-level section that should be highlighted,
 * enabling section-level disambiguation on top of the item-level matched flags.
 *
 * Two phases:
 *
 * Phase 1 — _ddc_nav_drawer present (user clicked a nav link):
 *   URL-only matching (no matched/selected flags) finds the one section whose
 *   descendant's buildNavUrl matches the page URL. Returns that section's id
 *   when exactly one section matches. When zero or multiple sections match,
 *   returns null and defers to the item-level matched flags as-is.
 *
 * Phase 2 — direct navigation (no _ddc_nav_drawer):
 *   Path-only matching (ignores query params). Returns the single section's id
 *   when exactly one section has a path-matching descendant. Returns null when
 *   zero or multiple sections match (ambiguous — show nothing / defer to flags).
 *
 * When this returns a non-null id, NavItem should treat ONLY that section as
 * active, overriding any matched-flag-driven active state on other sections.
 *
 * @param {NavigationItem[]} items - Top-level navigation items
 * @param {string} currentPath - Current page URL (pathname + search)
 * @returns {string|null} The id of the single winning section, or null
 */
export const resolveDirectNavActiveSectionId = (items, currentPath) => {
	if (!currentPath) return null;

	if (currentPath.includes('_ddc_nav_drawer=')) {
		// Phase 1: URL-only matching — bypasses matched/selected flags to find
		// the section containing the item whose link was actually clicked.
		const urlOnlyMatches = items.filter(
			(item) =>
				isNavItemActiveUrlOnly(item, currentPath) ||
				hasUrlOnlyActiveDescendant(item, currentPath)
		);
		// _ddc_nav_drawer values are unique per item so at most one section wins.
		// Return null (defer to matched flags) when no URL match is found.
		if (urlOnlyMatches.length === 1) return urlOnlyMatches[0].id ?? null;
		return null;
	}

	// Phase 2: direct navigation — path-only, exactly one section must match.
	const normalizedPagePath = normalizePath(currentPath);
	const pathMatches = items.filter(
		(item) =>
			isNavItemActivePathOnly(item, normalizedPagePath) ||
			hasPathOnlyActiveDescendant(item, normalizedPagePath)
	);
	if (pathMatches.length !== 1) return null;
	return pathMatches[0].id ?? null;
};

/**
 * Returns true if the navigation item should be rendered in the drawer.
 * Filters out embedded items (NavFragment references) and items with
 * missing or whitespace-only labels.
 *
 * @param {NavigationItem} item
 * @returns {boolean}
 */
/** @type {IsVisibleNavItem} */
export const isVisibleNavItem = (item) => {
	if (!item) return false;
	if (item.embedded) return false;
	if (!item.label || !item.label.trim()) return false;
	return true;
};

/**
 * Returns true if any descendant of the given navigation item is active.
 * Recursively walks item.navigationItems. Separate from isNavItemActive
 * (Single Responsibility: "is the current page UNDER this item?").
 *
 * @param {NavigationItem} item - Navigation item to check descendants of
 * @param {string} [currentPath] - Current URL path. Defaults to window.location.pathname.
 * @returns {boolean}
 */
export const hasActiveDescendant = (item, currentPath) => {
	if (!item) return false;
	const children = safeNavItems(item);
	return children.some(
		(child) =>
			isNavItemActive(child, currentPath) ||
			hasActiveDescendant(child, currentPath)
	);
};

/**
 * Returns true if the navigation item is a group heading.
 * A group heading has url === HASH_URL_SENTINEL and at least one child navigationItem.
 *
 * @param {NavigationItem} item
 * @returns {boolean}
 */
/** @type {IsGroupHeading} */
export const isGroupHeading = (item) => {
	if (!item) return false;
	return item.url === HASH_URL_SENTINEL && safeNavItems(item).length > 0;
};

/**
 * Returns true if the navigation item is a fragment-wrapper — a group whose
 * sole purpose is to hold embedded fragment children.
 *
 * Criteria: url === HASH_URL_SENTINEL AND at least one child exists AND every child has
 * embedded === true.
 *
 * @param {NavigationItem} item
 * @returns {boolean}
 */
export const isFragmentWrapper = (item) => {
	if (!item) return false;
	if (item.url !== HASH_URL_SENTINEL) return false;
	const children = safeNavItems(item);
	if (children.length === 0) return false;
	return children.every((child) => child.embedded === true);
};

/**
 * Returns a flat array of visible leaf nav items for a drilldown panel.
 * Group headings (url === '#') are transparent containers — their children
 * are inlined in place of the heading itself.
 *
 * Used by SSRDrilldownLinks to produce a flat list of crawlable <a> links
 * without embedding traversal logic in JSX.
 *
 * @param {NavigationItem} parent - Top-level nav item with drilldown children
 * @returns {NavigationItem[]}
 */
export const flattenDrilldownChildren = (parent) =>
	safeNavItems(parent)
		.filter(isVisibleNavItem)
		.flatMap((child) =>
			isGroupHeading(child)
				? safeNavItems(child).filter(isVisibleNavItem)
				: [child]
		);

/**
 * Returns true when a label string contains HTML tags or HTML character
 * entities (named, decimal, or hex). Use this to decide whether a label
 * needs sanitization before rendering.
 *
 * @param {string} label
 * @returns {boolean}
 */
export const containsHtmlContent = (label) =>
	/<[^>]+>/.test(label) || /&(?:[a-z]+|#\d+|#x[0-9a-f]+);/i.test(label);

/**
 * Sanitizes a nav item label for safe rendering.
 *
 * - Returns null when label is falsy or blank.
 * - Returns the original string unchanged when it contains no HTML tags
 *   or HTML entities (fast path — the vast majority of labels are plain text).
 * - When the label contains HTML or entities, parses it with DOMParser, strips
 *   <script> and <style> elements, removes inline event-handler attributes (on*),
 *   javascript: URIs, and non-image data: URIs on any attribute, then returns
 *   document.body.innerHTML.
 * - Falls back to the raw string when window is undefined (SSR guard).
 *
 * @param {string} label
 * @returns {string|null}
 */
export const sanitizeNavLabel = (label) => {
	if (!label || !label.trim()) return null;
	if (!containsHtmlContent(label)) return label;
	if (typeof window === 'undefined') return label;
	const doc = new DOMParser().parseFromString(label, 'text/html');
	doc.querySelectorAll('script, style').forEach((el) => el.remove());
	/* eslint-disable no-script-url */
	doc.querySelectorAll('*').forEach((el) => {
		Array.from(el.attributes).forEach((attr) => {
			if (attr.name.startsWith('on')) {
				el.removeAttribute(attr.name);
			} else if (
				/^javascript:/i.test(attr.value.trim()) ||
				/^data:(?!image\/)/i.test(attr.value.trim())
			) {
				el.removeAttribute(attr.name);
			}
		});
	});
	/* eslint-enable no-script-url */
	return doc.body.innerHTML;
};
